/* oxlint-disable jsx-a11y/no-noninteractive-tabindex -- focusable modal overflow lets keyboard users scroll long dialog content */
import type React from 'react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  showCloseButton?: boolean
}

let modalIdCounter = 0
const openModalIds: string[] = []
const stackListeners = new Set<() => void>()

const updateBodyOverflow = () => {
  document.body.style.overflow = openModalIds.length > 0 ? 'hidden' : ''
  const appRoot = document.getElementById('root')
  if (openModalIds.length > 0) {
    appRoot?.setAttribute('inert', '')
  } else {
    appRoot?.removeAttribute('inert')
  }
}

const notifyStackChange = () => {
  updateBodyOverflow()
  stackListeners.forEach((listener) => listener())
}

const registerModal = (modalId: string) => {
  if (openModalIds.includes(modalId)) return
  openModalIds.push(modalId)
  notifyStackChange()
}

const unregisterModal = (modalId: string) => {
  const modalIndex = openModalIds.indexOf(modalId)
  if (modalIndex === -1) return

  openModalIds.splice(modalIndex, 1)
  notifyStackChange()
}

const subscribeToStackChanges = (listener: () => void) => {
  stackListeners.add(listener)

  return () => {
    stackListeners.delete(listener)
  }
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  showCloseButton = true,
}) => {
  const { t } = useTranslation()
  const [, setStackVersion] = useState(0)
  const backdropRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const invokerRef = useRef<HTMLElement | null>(null)
  const modalIdRef = useRef(`modal-${++modalIdCounter}`)
  const modalId = modalIdRef.current
  const titleId = `${modalId}-title`

  useLayoutEffect(() => {
    if (!isOpen) return

    invokerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null

    const unsubscribe = subscribeToStackChanges(() => {
      setStackVersion((currentVersion) => currentVersion + 1)
    })

    registerModal(modalId)
    setStackVersion((currentVersion) => currentVersion + 1)

    return () => {
      unsubscribe()
      unregisterModal(modalId)
      const invoker = invokerRef.current
      if (invoker?.isConnected) invoker.focus()
    }
  }, [isOpen, modalId])

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }

  const stackIndex = openModalIds.indexOf(modalId)
  const topStackIndex = openModalIds.length - 1
  const effectiveStackIndex = stackIndex === -1 ? openModalIds.length : stackIndex
  const effectiveTopStackIndex = Math.max(topStackIndex, effectiveStackIndex)
  const isTopModal = effectiveStackIndex === effectiveTopStackIndex
  const depthFromTop = Math.max(0, effectiveTopStackIndex - effectiveStackIndex)
  const nestedOffsetY = Math.min(depthFromTop * 18, 54)
  const nestedScale = Math.max(1 - depthFromTop * 0.02, 0.92)
  const nestedOpacity = Math.max(1 - depthFromTop * 0.16, 0.55)
  const zIndex = 50 + effectiveStackIndex * 10

  useEffect(() => {
    if (!isOpen || !isTopModal) return
    // Move focus after the browser has applied `inert` to the app root. Focusing in
    // the same frame can otherwise be discarded when the invoker is made inert.
    const focusTimer = window.setTimeout(() => {
      const initialFocus =
        titleRef.current ??
        contentRef.current?.querySelector<HTMLElement>(
          '[autofocus], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        ) ??
        contentRef.current
      ;(initialFocus ?? backdropRef.current)?.focus()
    })

    return () => window.clearTimeout(focusTimer)
  }, [isOpen, isTopModal])

  const handleBackdropKeyDown = (event: React.KeyboardEvent) => {
    if (!isTopModal) return

    if (event.key === 'Escape') {
      onClose()
    }
  }

  const handleContentKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape' && isTopModal) {
      onClose()
    }

    if (event.key === 'Tab' && isTopModal && contentRef.current) {
      const focusable = [
        contentRef.current,
        ...Array.from(
          contentRef.current.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          )
        ),
      ].filter((element) => element.tabIndex >= 0 && element.getAttribute('aria-hidden') !== 'true')

      if (focusable.length === 0) {
        event.preventDefault()
        backdropRef.current?.focus()
      } else {
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (
          event.shiftKey &&
          (document.activeElement === first || document.activeElement === titleRef.current)
        ) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    event.stopPropagation()
  }

  if (!isOpen) return null

  return createPortal(
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the dialog owns backdrop dismissal and focus trapping
    <div
      ref={backdropRef}
      tabIndex={isTopModal ? -1 : undefined}
      inert={isTopModal ? undefined : true}
      className={`fixed inset-0 flex items-center justify-center p-4 py-5 ${isTopModal ? '' : 'pointer-events-none'}`}
      style={{
        zIndex,
        backgroundColor: isTopModal ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0)',
      }}
      onClick={
        isTopModal
          ? (event) => {
              if (event.target === event.currentTarget) onClose()
            }
          : undefined
      }
      onKeyDown={handleBackdropKeyDown}
      role="dialog"
      aria-modal={isTopModal ? 'true' : undefined}
      aria-labelledby={title ? titleId : undefined}
      data-modal-stack-index={effectiveStackIndex}
      data-modal-depth={depthFromTop}
      data-modal-top={isTopModal ? 'true' : 'false'}
    >
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- content stops backdrop clicks and nested keyboard events */}
      <div
        ref={contentRef}
        tabIndex={isTopModal ? 0 : -1}
        className={`my-5 max-h-[90vh] w-full overflow-y-auto rounded bg-white shadow-xl transition-[transform,opacity] duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-focus ${sizeClasses[size]}`}
        style={{
          transform: isTopModal
            ? 'translateY(0px) scale(1)'
            : `translateY(-${nestedOffsetY}px) scale(${nestedScale})`,
          opacity: isTopModal ? 1 : nestedOpacity,
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleContentKeyDown}
        role="document"
      >
        {title && (
          <div
            className={`relative flex w-full items-center justify-center border-0 py-modal-header-padding-y text-center ${isTopModal ? 'bg-brand-surface text-brand-ink' : 'bg-brand-surface text-text-gray-500'}`}
          >
            <h2
              ref={titleRef}
              id={titleId}
              tabIndex={-1}
              className="text-modal-title m-0 w-full font-semibold focus:outline-none"
            >
              {title}
            </h2>
            {showCloseButton && (
              <button
                type="button"
                onClick={() => {
                  if (!isTopModal) return
                  onClose()
                }}
                className={`absolute top-1/2 right-1 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-button border-button border-transparent bg-transparent p-2 transition-opacity ${isTopModal ? 'cursor-pointer text-brand-ink hover:bg-white/70' : 'cursor-default text-text-gray-500'}`}
                aria-label={t('modal.closeAriaLabel')}
                disabled={!isTopModal}
              >
                <svg
                  className="size-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>
        )}
        <div
          className={isTopModal ? 'p-modal-padding' : 'hidden'}
          data-modal-content-visible={isTopModal ? 'true' : 'false'}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  )
}
